import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import {
  Camera,
  useCameraDevice,
  useCameraPermission,
  useCodeScanner,
} from 'react-native-vision-camera';
import { AppButton } from './AppButton';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';

type Props = {
  onScanned: (value: string) => void;
  onCancel: () => void;
};

function extractActivationToken(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    const parsed = JSON.parse(trimmed) as { activationToken?: unknown };
    if (typeof parsed.activationToken === 'string' && parsed.activationToken.trim()) {
      return parsed.activationToken.trim();
    }
  } catch {
    // Plain token string from QR.
  }
  return trimmed;
}

export function KioskActivationScanner({ onScanned, onCancel }: Props) {
  const palette = useAppColors();
  const device = useCameraDevice('back');
  const { hasPermission, requestPermission } = useCameraPermission();
  const [permissionAsked, setPermissionAsked] = useState(false);
  const handled = useRef(false);

  const handleCode = useCallback(
    (value: string) => {
      if (handled.current) return;
      const token = extractActivationToken(value);
      if (!token) return;
      handled.current = true;
      onScanned(token);
    },
    [onScanned],
  );

  const codeScanner = useCodeScanner({
    codeTypes: ['qr'],
    onCodeScanned: codes => {
      const value = codes[0]?.value;
      if (typeof value === 'string') handleCode(value);
    },
  });

  const themed = useMemo(
    () => ({
      title: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700' as const,
        color: palette.white,
        textAlign: 'center' as const,
      },
      body: {
        ...typography.body,
        color: palette.white,
        textAlign: 'center' as const,
        opacity: 0.9,
      },
    }),
    [palette],
  );

  if (!hasPermission) {
    return (
      <View style={[styles.center, { backgroundColor: palette.background }]}>
        <Text style={[themed.title, { color: palette.textPrimary }]}>
          Camera permission needed
        </Text>
        <Text style={[themed.body, { color: palette.textSecondary }]}>
          Allow camera access to scan the activation QR code, or paste the token
          instead.
        </Text>
        <AppButton
          title={permissionAsked ? 'Try again' : 'Allow camera'}
          onPress={() => {
            setPermissionAsked(true);
            requestPermission().catch(() => undefined);
          }}
        />
        <AppButton title="Cancel" variant="secondary" onPress={onCancel} />
      </View>
    );
  }

  if (!device) {
    return (
      <View style={[styles.center, { backgroundColor: palette.background }]}>
        <Text style={[themed.title, { color: palette.textPrimary }]}>
          Camera unavailable
        </Text>
        <Text style={[themed.body, { color: palette.textSecondary }]}>
          Paste the activation token instead.
        </Text>
        <AppButton title="Cancel" variant="secondary" onPress={onCancel} />
      </View>
    );
  }

  return (
    <View style={styles.host}>
      <Camera
        style={StyleSheet.absoluteFill}
        device={device}
        isActive
        codeScanner={codeScanner}
      />
      <View style={styles.overlay}>
        <Text style={themed.title}>Scan activation QR</Text>
        <Text style={themed.body}>
          Point the camera at the QR from the admin portal.
        </Text>
        <View style={styles.frame} />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Cancel scan"
          onPress={onCancel}
          style={styles.cancel}
        >
          <Text style={themed.body}>Cancel</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: { flex: 1, backgroundColor: '#000' },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
    gap: spacing.md,
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.lg,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  frame: {
    width: 240,
    height: 240,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    borderRadius: 16,
  },
  cancel: {
    minHeight: 44,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
